import api from "./api"
import { RegistrarLogErro } from "./clientefiel"

const authHeader = (authorization: string) => ({
    "Content-Type": "application/json",
    Authorization: authorization,
})

export interface ErroApi {
    retornoErro: number
    mensagem: string
}

type RespostaRaw<T> = T & { codErro?: number; mensagem?: string }

function tratamentoResponse<T>(response: { data: RespostaRaw<T> }, chamada: string): T | ErroApi {
    const { data } = response
    if (data && (data as { codErro?: number }).codErro !== undefined && data.codErro! > 0) {
        return { retornoErro: data.codErro!, mensagem: data.mensagem ?? "" }
    }
    if (typeof data === "string" && data !== "") {
        return {
            retornoErro: 1,
            mensagem: `${chamada} - Erro inesperado, entre em contato com os responsáveis.`,
        }
    }
    return data
}

function erroParaRespostaRaw(error: unknown): RespostaRaw<never> {
    const axiosError = error as { message?: string; response?: { data?: unknown } }
    const data = axiosError.response?.data
    if (
        typeof data === "object" &&
        data !== null &&
        "codErro" in data &&
        (data as { codErro: number }).codErro > 0
    ) {
        const d = data as { codErro: number; mensagem?: string }
        return { codErro: d.codErro, mensagem: d.mensagem } as RespostaRaw<never>
    }
    if (axiosError.message === "Network Error") {
        return { codErro: 1, mensagem: "Verifique sua conexão!" } as RespostaRaw<never>
    }
    return { codErro: 1, mensagem: axiosError.message || "Erro desconhecido" } as RespostaRaw<never>
}

// ─── Modelos ─────────────────────────────────────────────────────────────────

/** Tipos de recurso conhecidos pelo código (espelha o enum TipoRecursoAssinatura do backend). */
export type TipoRecursoAssinatura = "ROTEIRIZACAO" | "EMISSAO_FISCAL" | "ENTREGA_POR_DISTANCIA" | string

/** Um recurso adicional contratado por uma assinatura. */
export interface RecursoAssinatura {
    id: number
    tipo: TipoRecursoAssinatura
    descricao: string
    valor: number
    ativo: boolean
    /** Epoch millis da contratação (ou null). */
    dataCriacao: number | null
}

/** Item do catálogo (lista de recursos disponíveis, vinda do banco). */
export interface CatalogoRecurso {
    tipo: TipoRecursoAssinatura
    descricao: string
    valorPadrao: number
}

/** Visão completa da assinatura do estabelecimento. */
export interface AssinaturaResumo {
    estabelecimento: { id: number; nome: string }
    possuiAssinatura: boolean
    valorBase: number
    recursos: RecursoAssinatura[]
    valorRecursos: number
    valorMensalTotal: number
    catalogo: CatalogoRecurso[]
    /** true apenas para o perfil CLIENTEFIEL (pode negociar/editar valores e remover). */
    podeEditarValor: boolean
}

/** Corpo enviado ao salvar/adicionar um recurso. */
export interface RecursoInput {
    /** Presente => ajuste de valor de um recurso existente (CLIENTEFIEL). */
    id?: number
    /** Obrigatório ao adicionar um novo recurso. */
    tipo?: TipoRecursoAssinatura
    /** Valor negociado; ignorado pelo servidor quando o perfil não pode editar valor. */
    valor?: number
}

// ─── Chamadas ────────────────────────────────────────────────────────────────

export const obterAssinatura = async function (
    authorization: string
): Promise<AssinaturaResumo | ErroApi> {
    try {
        const response = await api
            .get<RespostaRaw<AssinaturaResumo>>("assinatura/recursos", {
                headers: authHeader(authorization),
            })
            .catch(error => ({ data: erroParaRespostaRaw(error) }))
        return tratamentoResponse<AssinaturaResumo>(response, "obterAssinatura")
    } catch (error) {
        RegistrarLogErro(error, "obterAssinatura", "")
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}

export const salvarRecurso = async function (
    authorization: string,
    recurso: RecursoInput
): Promise<AssinaturaResumo | ErroApi> {
    try {
        const response = await api
            .post<RespostaRaw<AssinaturaResumo>>("assinatura/recursos/salvar", recurso, {
                headers: authHeader(authorization),
            })
            .catch(error => ({ data: erroParaRespostaRaw(error) }))
        return tratamentoResponse<AssinaturaResumo>(response, "salvarRecurso")
    } catch (error) {
        RegistrarLogErro(error, "salvarRecurso", JSON.stringify(recurso))
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}

export const removerRecurso = async function (
    authorization: string,
    id: number
): Promise<AssinaturaResumo | ErroApi> {
    try {
        const response = await api
            .post<RespostaRaw<AssinaturaResumo>>(
                `assinatura/recursos/remover/${id}`,
                {},
                { headers: authHeader(authorization) }
            )
            .catch(error => ({ data: erroParaRespostaRaw(error) }))
        return tratamentoResponse<AssinaturaResumo>(response, "removerRecurso")
    } catch (error) {
        RegistrarLogErro(error, "removerRecurso", String(id))
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}

/**
 * Consulta se o estabelecimento (loja em uso, resolvida pelo header) possui um recurso ativo.
 * Resiliente: qualquer erro/indisponibilidade retorna false (mantém o comportamento restrito),
 * então é seguro usar como gate de funcionalidade (ex.: roteirização ilimitada).
 */
export const possuiRecursoAssinatura = async function (
    authorization: string,
    tipo: TipoRecursoAssinatura
): Promise<boolean> {
    try {
        const response = await api
            .get<RespostaRaw<{ ativo?: boolean }>>(`assinatura/possui-recurso/${tipo}`, {
                headers: authHeader(authorization),
            })
            .catch(error => ({ data: erroParaRespostaRaw(error) }))
        const dados = tratamentoResponse<{ ativo?: boolean }>(response, "possuiRecursoAssinatura")
        if ("retornoErro" in dados) return false
        return dados.ativo === true
    } catch (error) {
        RegistrarLogErro(error, "possuiRecursoAssinatura", String(tipo))
        return false
    }
}
